<template>
  <NuxtLayout
    :name="layoutName"
    :navbar-color="layoutNavbarColor"
    :footer-color="layoutFooterColor"
  >
    <div v-if="story">
      <section class="reports__hero">
        <HeroResourcesHub add-content-spacing>
          <template #subheading>
            {{ $t('common.resources_hub_hero.reports.subheading') }}
          </template>
          <template #title>
            {{ $t('common.resources_hub_hero.reports.title') }}
          </template>
          <template #text>
            {{ $t('common.resources_hub_hero.reports.text') }}
          </template>
          <template #image>
            <img
              src="/images/resources-hub/reports-hub.png"
              alt="Reports Hub"
              class="reports__image"
            />
          </template>
        </HeroResourcesHub>
        <div class="container">
          <SbResourcesHubNav />
        </div>
      </section>
      <ReportsCardsSection :reports="reportsStories?.reports.data.stories" />
      <StoryblokComponent
        v-for="body in story.content.body"
        :key="body._uid"
        :blok="body"
      />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { SbReportTag } from '@/types'
const reportsStore = useReportsStore()
const { tagStories } = storeToRefs(reportsStore)
const mainStore = useMainStore()

const { availableLanguages } = storeToRefs(mainStore)

const config = useRuntimeConfig()
const storyblokApi = useStoryblokApi()
const { locale } = useI18n()

availableLanguages.value = ['en', 'de']

const [{ data: reportsStories }, { story }] = await Promise.all([
  useAsyncData(`data-${locale.value}`, async () => {
    const reports = await storyblokApi.get('cdn/stories', {
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      starts_with: 'reports/',
      resolve_relations: ['sb-page-report.tags'],
      language: locale.value,
      per_page: 100,
      filter_query: {
        component: {
          in: 'sb-page-report',
        },
      },
    })

    const tags = await storyblokApi.get('cdn/stories', {
      version: config.public.storyblokApiVersion as 'draft' | 'published',
      language: locale.value,
      starts_with: 'reports/report-tags',
      filter_query: {
        component: {
          in: 'report-tag',
        },
      },
    })

    return {
      reports,
      tags,
    }
  }),
  useFetchStory('reports'),
])
const { layoutName, layoutNavbarColor, layoutFooterColor } =
  useStoryLayout<any>(story)
onMounted(() => {
  tagStories.value = reportsStories.value?.tags?.data.stories.sort(
    (a: SbReportTag, b: SbReportTag) =>
      a.content.name.localeCompare(b.content.name),
  )
})

useCustomHead(story)
</script>

<style lang="scss" scoped>
.reports {
  &__hero {
    padding: 80px 0 48px 0;
  }

  &__image {
    width: 100%;
    max-width: 320px;
    margin-left: auto;
    margin-right: 0;

    @media (min-width: $breakpoint-lg) {
      position: absolute;
      right: -30px;
      top: 0;
    }
  }
}
</style>
